import { useEffect, useState, useRef } from "react";
import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "./App.css";
import { ChevronDown } from "lucide-react";
import { KeyGate } from "./components/KeyGate";
import { AdminDashboard } from "./components/AdminDashboard";
import { PrivacyPolicy, CookiePolicy } from "./components/Policies";

const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  
  return (
    <motion.div
      className="scroll-progress"
      style={{ scaleX: scrollYProgress }}
    />
  );
};

const Navigation = ({ onAdminClick, isAdmin }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY && currentScrollY > 100) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY || currentScrollY < 50) {
        setIsVisible(false);
        setMenuOpen(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const navItems = [
    { id: 'philosophy', label: 'Philosophy' },
    { id: 'membership', label: 'Membership' },
    { id: 'access', label: 'Access' },
    { id: 'admission', label: 'Admission' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <nav className={`hidden-nav ${isVisible ? 'visible' : ''}`}>
      <div className="nav-container">
        <a
          href="#hero"
          className="nav-logo"
          onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }}
        >
          <img 
            src="https://static.wixstatic.com/media/c29c6a_7a7ac256802147668569a8dde18c3fac~mv2.png/v1/fill/w_310,h_335,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/HNC%20Group%20-%20Logo_Gold_PNG.png" 
            alt="HnC Group"
            className="nav-logo-image"
          />
        </a>
        
        {/* Mobile menu button */}
        <button 
          className="mobile-menu-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span className={`menu-icon ${menuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        {/* Navigation links */}
        <ul className={`nav-links ${menuOpen ? 'mobile-open' : ''}`}>
          {navItems.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className="nav-link"
                onClick={(e) => { e.preventDefault(); scrollToSection(item.id); }}
              >
                {item.label}
              </a>
            </li>
          ))}
          {isAdmin && (
            <li>
              <button
                className="nav-link admin-link"
                onClick={() => { onAdminClick(); setMenuOpen(false); }}
              >
                Admin
              </button>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
};

const AnimatedText = ({ text, className }) => {
  const letters = text.split('');
  
  return (
    <span className={className}>
      {letters.map((letter, index) => (
        <motion.span
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.05,
            delay: index * 0.05
          }}
          style={{ display: 'inline-block' }}
        >
          {letter === ' ' ? '\u00A0' : letter}
        </motion.span>
      ))}
    </span>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 50]);

  return (
    <motion.section
      ref={ref}
      id="hero"
      className="hero-section texture-overlay"
      style={{ y }}
    >
      <div className="hero-content">
        <h1 className="hero-title">
          <AnimatedText text="Power. Protected." className="" />
        </h1>
        <motion.p
          className="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.8 }}
        >
          An unseen world for those who move silently
        </motion.p>
      </div>
      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 2.5 }}
      >
        <span>Discover</span>
        <ChevronDown size={16} strokeWidth={1} />
      </motion.div>
    </motion.section>
  );
};

const RevealSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, delay, ease: [0.645, 0.045, 0.355, 1] }}
    >
      {children}
    </motion.div>
  );
};

const GoldLine = ({ delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      className="gold-line"
      initial={{ scaleX: 0 }}
      animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
      transition={{ duration: 1, delay, ease: [0.645, 0.045, 0.355, 1] }}
      style={{ originX: 0 }}
    />
  );
};

const Philosophy = () => {
  return (
    <section id="philosophy" className="section-spacing">
      <div className="luxury-container">
        <div className="two-column">
          <RevealSection>
            <span className="section-label">Philosophy</span>
            <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '48px' }}>The Institution</h2>
            <GoldLine delay={0.3} />
          </RevealSection>
          <RevealSection delay={0.2}>
            <p className="section-description" style={{ margin: 0, marginBottom: '32px' }}>
              This is not a service provider. This is a private members' institution derived from principles of absolute discretion and sovereign authority.
            </p>
            <p className="section-description" style={{ margin: 0, marginBottom: '32px' }}>
              Access is granted by trust, not wealth. Privacy, security, and serenity are the real luxuries we protect.
            </p>
            <p className="section-description" style={{ margin: 0, color: 'var(--aged-gold)' }}>
              Membership is not requested. It is granted.
            </p>
          </RevealSection>
        </div>
      </div>
    </section>
  );
};

const WhatIsHnC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="what-is-hnc" className="section-spacing" style={{ background: 'var(--bg-black)' }}>
      <div className="luxury-container">
        <RevealSection>
          <div className="section-header">
            <span className="section-label">What is the HnC Private Club</span>
            <h2 className="section-title">Beyond Transactions</h2>
            <p className="section-description">
              Members do not pay per service. They hold a monthly membership. They deposit a redeemable retainer.
              Requests are executed quietly on their behalf.
            </p>
          </div>
        </RevealSection>
        <motion.p
          ref={ref}
          className="institutional-quote"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.645, 0.045, 0.355, 1] }}
        >
          "Members do not spend. They reallocate resources within their circle of trust."
        </motion.p>
      </div>
    </section>
  );
};

const Membership = () => {
  const tiers = [
    {
      tier: "Entry Level",
      title: "Elite",
      features: [
        "Foundational access",
        "Essential protection services",
        "Lifestyle coordination",
        "Quarterly gatherings",
        "Trusted network introduction"
      ]
    },
    {
      tier: "Advanced",
      title: "Executive",
      features: [
        "Elevated privileges",
        "Dedicated liaison officer",
        "Priority response protocols",
        "International reach",
        "Strategic advisement"
      ]
    },
    {
      tier: "Sovereign",
      title: "Sovereign",
      features: [
        "Absolute discretion",
        "24/7 global response",
        "Council-level access",
        "Legacy protection",
        "Unlimited coordination"
      ]
    }
  ];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="membership" className="section-spacing">
      <div className="luxury-container">
        <RevealSection>
          <div className="section-header">
            <span className="section-label">Membership Structure</span>
            <h2 className="section-title">Levels of Access</h2>
          </div>
        </RevealSection>
        <div ref={ref} className="membership-grid">
          {tiers.map((tier, index) => (
            <motion.div
              key={index}
              className="membership-card"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{
                duration: 0.8,
                delay: isInView ? 0.2 + (index * 0.15) : 0,
                ease: [0.645, 0.045, 0.355, 1]
              }}
            >
              <div className="membership-tier">{tier.tier}</div>
              <h3 className="membership-title">{tier.title}</h3>
              <ul className="membership-features">
                {tier.features.map((feature, i) => (
                  <li key={i} className="membership-feature">{feature}</li>
                ))}
              </ul>
              <div className="membership-note">Upon invitation</div>
            </motion.div>
          ))}
        </div>
        <motion.p
          className="institutional-quote"
          style={{ fontSize: '20px', marginTop: '80px' }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, delay: 0.8, ease: [0.645, 0.045, 0.355, 1] }}
        >
          An inner circle exists beyond Sovereign. It is not displayed.
        </motion.p>
      </div>
    </section>
  );
};

const Retainer = () => {
  return (
    <section id="retainer" className="section-spacing" style={{ background: 'var(--bg-black)' }}>
      <div className="luxury-container">
        <div className="two-column">
          <RevealSection>
            <span className="section-label">Redeemable Retainer</span>
            <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '48px' }}>Peace of Mind, Structured</h2>
            <GoldLine delay={0.3} />
          </RevealSection>
          <RevealSection delay={0.2}>
            <p className="section-description" style={{ margin: 0, marginBottom: '32px' }}>
              Members deposit a monthly retainer. Funds are fully or partially redeemable.
            </p>
            <p className="section-description" style={{ margin: 0, marginBottom: '32px' }}>
              Used for flights, hotels, gifts, experiences, security logistics, and lifestyle management.
            </p>
            <p className="section-description" style={{ margin: 0, color: 'var(--aged-gold)', fontStyle: 'italic' }}>
              Once the retainer is set, the world aligns quietly around you.
            </p>
          </RevealSection>
        </div>
      </div>
    </section>
  );
};

const Access = () => {
  const pillars = [
    {
      title: "Protection",
      items: [
        "Close protection",
        "Relocation services",
        "Medical evacuation",
        "Risk & intelligence",
        "Crisis management"
      ]
    },
    {
      title: "Lifestyle",
      items: [
        "Private travel",
        "Art & collectibles",
        "Rare experiences",
        "Event access",
        "Acquisitions"
      ]
    },
    {
      title: "Network",
      items: [
        "Private introductions",
        "Off-market opportunities",
        "Intelligence briefings",
        "Real estate access",
        "Family office liaison"
      ]
    }
  ];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="access" className="section-spacing">
      <div className="luxury-container">
        <RevealSection>
          <div className="section-header">
            <span className="section-label">Areas of Access</span>
            <h2 className="section-title">Three Pillars</h2>
          </div>
        </RevealSection>
        <div ref={ref} className="pillars-grid">
          {pillars.map((pillar, pillarIndex) => (
            <motion.div
              key={pillarIndex}
              className="pillar"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{
                duration: 0.8,
                delay: pillarIndex * 0.15,
                ease: [0.645, 0.045, 0.355, 1]
              }}
            >
              <h3 className="pillar-title">{pillar.title}</h3>
              <ul className="pillar-items">
                {pillar.items.map((item, itemIndex) => (
                  <motion.li
                    key={itemIndex}
                    className="pillar-item"
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                    transition={{
                      duration: 0.5,
                      delay: (pillarIndex * 0.15) + 0.3 + (itemIndex * 0.1),
                      ease: [0.645, 0.045, 0.355, 1]
                    }}
                  >
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Admission = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="admission" className="section-spacing" style={{ background: 'var(--bg-black)' }}>
      <div className="luxury-container">
        <RevealSection>
          <div className="section-header">
            <span className="section-label">Admission</span>
            <h2 className="section-title">By Invitation Only</h2>
            <p className="section-description">
              Entry only by member referral or council approval. Every member is responsible for who they introduce.
            </p>
          </div>
        </RevealSection>
        <motion.p
          ref={ref}
          className="institutional-quote"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.645, 0.045, 0.355, 1] }}
        >
          "If you have been referred, you already know how to proceed."
        </motion.p>
      </div>
    </section>
  );
};

const Governance = () => {
  return (
    <section id="governance" className="section-spacing">
      <div className="luxury-container">
        <div className="two-column">
          <RevealSection>
            <span className="section-label">Governance</span>
            <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '48px' }}>The HnC Council</h2>
            <GoldLine delay={0.3} />
          </RevealSection>
          <RevealSection delay={0.2}>
            <p className="section-description" style={{ margin: 0, marginBottom: '32px' }}>
              Three divisions govern the institution: Protection, Intelligence, and Luxury.
            </p>
            <p className="section-description" style={{ margin: 0, marginBottom: '32px' }}>
              They safeguard ethics, admissions, confidentiality, and culture.
            </p>
            <p className="section-description" style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px', fontStyle: 'italic' }}>
              Decisions are deliberate. Authority is absolute. Accountability is internal.
            </p>
          </RevealSection>
        </div>
      </div>
    </section>
  );
};

const Legacy = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="legacy" className="section-spacing" style={{ background: 'var(--bg-black)' }}>
      <div className="luxury-container">
        <RevealSection>
          <div className="section-header">
            <span className="section-label">Ritual & Legacy</span>
            <h2 className="section-title">The Admission Ritual</h2>
            <p className="section-description">
              Upon acceptance, each member receives a handwritten letter. No publicity. No announcements.
            </p>
          </div>
        </RevealSection>
        <motion.p
          ref={ref}
          className="institutional-quote"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.645, 0.045, 0.355, 1] }}
        >
          "Belonging is never announced. It is recognised."
        </motion.p>
      </div>
    </section>
  );
};

const Contact = () => {
  return (
    <section id="contact" className="contact-minimal">
      <div className="luxury-container">
        <RevealSection>
          <span className="section-label" style={{ marginBottom: '32px', display: 'block' }}>Contact</span>
          <h2 className="section-title" style={{ marginBottom: '48px' }}>For Matters of Relevance</h2>
          <p className="section-description" style={{ marginBottom: '48px' }}>
            Discretion is assumed.
          </p>
          <a href="mailto:council@hnc-private.club" className="contact-email">
            council@hnc-private.club
          </a>
        </RevealSection>
      </div>
    </section>
  );
};

const BrochureDownload = () => {
  return (
    <section id="brochure" className="brochure-section">
      <div className="luxury-container">
        <RevealSection>
          <span className="section-label" style={{ marginBottom: '32px', display: 'block' }}>Resources</span>
          <h2 className="section-title" style={{ marginBottom: '32px' }}>Detailed Information</h2>
          <p className="section-description" style={{ marginBottom: '48px' }}>
            A comprehensive overview of our services and capabilities.
          </p>
          <a 
            href="/HNC_Group_Brochure_2026.pdf" 
            download
            className="brochure-download-btn"
          >
            Download Brochure
          </a>
        </RevealSection>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ onPrivacyClick, onCookieClick }) => {
  return (
    <footer className="site-footer">
      <div className="footer-container">
        <div className="footer-links">
          <button className="footer-link" onClick={onPrivacyClick}>
            Privacy Policy
          </button>
          <button className="footer-link" onClick={onCookieClick}>
            Cookie Policy
          </button>
        </div>
        <p className="footer-copyright">
          © {new Date().getFullYear()} HNC Group. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// Main Site Component
const MainSite = ({ userType }) => {
  const navigate = useNavigate();
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCookie, setShowCookie] = useState(false);
  
  const handleAdminClick = () => {
    if (userType === 'admin') {
      navigate('/admin');
    }
  };

  if (showPrivacy) {
    return <PrivacyPolicy onClose={() => setShowPrivacy(false)} />;
  }

  if (showCookie) {
    return <CookiePolicy onClose={() => setShowCookie(false)} />;
  }

  return (
    <>
      <ScrollProgress />
      <Navigation onAdminClick={handleAdminClick} isAdmin={userType === 'admin'} />
      <Hero />
      <Philosophy />
      <WhatIsHnC />
      <Membership />
      <Retainer />
      <Access />
      <Admission />
      <Governance />
      <Legacy />
      <Contact />
      <Footer 
        onPrivacyClick={() => setShowPrivacy(true)} 
        onCookieClick={() => setShowCookie(true)} 
      />
    </>
  );
};

// Admin Page Component
const AdminPage = ({ userType, onLogout }) => {
  const navigate = useNavigate();
  
  if (userType !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <AdminDashboard onClose={() => navigate('/')} onLogout={onLogout} />;
};

// App Content with routing logic
const AppContent = () => {
  const [hasAccess, setHasAccess] = useState(false);
  const [userType, setUserType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  // Clear any existing session on mount - ALWAYS show Key Gate on new visit
  useEffect(() => {
    const clearSession = async () => {
      // Clear sessionStorage
      sessionStorage.removeItem('hnc_token');
      sessionStorage.removeItem('hnc_user_type');
      setIsLoading(false);
    };
    
    clearSession();
  }, []);

  const handleAccessGranted = (data) => {
    setHasAccess(true);
    setUserType(data.user_type);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('hnc_token');
    sessionStorage.removeItem('hnc_user_type');
    setHasAccess(false);
    setUserType(null);
  };

  if (isLoading) {
    return <div className="App" style={{ background: 'var(--bg-black)' }} />;
  }

  // ALWAYS show KeyGate if not authenticated in this session
  if (!hasAccess) {
    return (
      <div className="App">
        <KeyGate onAccessGranted={handleAccessGranted} />
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<MainSite userType={userType} />} />
        <Route path="/admin" element={<AdminPage userType={userType} onLogout={handleLogout} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;